import * as React from "react"

import {
  DropdownMenuItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { Permission, Workspace, Connection } from "@/types"
import { usePermissionCheck } from "@/hooks"

export interface PermissionMenuItemProps extends React.ComponentProps<
  typeof DropdownMenuItem
> {
  /** The permission required to enable this menu item */
  permission: Permission
  /** Workspace to check permissions against */
  workspace?: Workspace
  /** Connection to check permissions against */
  connection?: Connection
  /** Tooltip message to show when user lacks permission */
  disabledTooltip?: string
}

/**
 * A DropdownMenuItem that automatically handles permission checks.
 * When the user lacks the required permission:
 * - The menu item is disabled
 * - A tooltip shows the reason (if disabledTooltip is provided)
 */
const PermissionMenuItem = ({
  permission,
  workspace,
  connection,
  disabledTooltip,
  disabled,
  className,
  children,
  ...menuItemProps
}: PermissionMenuItemProps) => {
  const { hasPermission, isLoading } = usePermissionCheck({
    permission,
    workspace,
    connection,
  })

  const isDisabled = disabled || isLoading || !hasPermission
  const showTooltip = isDisabled && !hasPermission && disabledTooltip

  const menuItem = (
    <DropdownMenuItem
      {...menuItemProps}
      disabled={isDisabled}
      className={cn(
        className,
        isDisabled && !hasPermission && "cursor-not-allowed"
      )}
    >
      {children}
    </DropdownMenuItem>
  )

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block w-full">{menuItem}</span>
        </TooltipTrigger>
        <TooltipContent side="left">{disabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  return menuItem
}

export default PermissionMenuItem
